import {
  addBankAccountV2,
  AdminWalletBankResponse,
  CreateManualDeliveryAddressData,
  editBankAccountV2,
  ErrorToast,
  isAdmin,
  isEmailValid,
  isPartner,
  SuccessToast,
  TaxClassification,
  useAuth,
  User,
  userAddBankAccountV2,
  userAddBankAccountV2Partner,
  UserRole,
  WalletBankV2Response,
  Wallets,
  reportError,
} from '@vgrubs/components';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { isValidPhoneNumber } from 'react-phone-number-input';

export enum Step {
  AboutYou = 1,
  BusinessInfo = 2,
  BankInfo = 3,
  Confirmation = 4,
}

export enum AccountType {
  individual = 'individual',
  business = 'business',
}

export enum BusinessType {
  soleProprietor = 'soleProprietor',
  llc = 'llc',
  corporation = 'corporation',
  partnership = 'partnership',
  trust = 'trust',
  estate = 'estate',
}

export const useWalletV2 = ({
  setModalIsOpen,
  wallet,
  setWallet,
  modalIsOpen,
  userId,
  userRole,
  user,
}: {
  setModalIsOpen: Dispatch<SetStateAction<Wallets | boolean | null>>;
  wallet: Wallets | AdminWalletBankResponse;
  setWallet: Dispatch<
    SetStateAction<WalletBankV2Response | AdminWalletBankResponse | null>
  >;
  modalIsOpen: Wallets | boolean | null;
  userId?: number;
  userRole: UserRole;
  user?: User;
}) => {
  const [loading, setLoading] = useState(false);

  const [termsIsOpen, setTermsIsOpen] = useState(false);
  const [initialBankModal, setInitialBankModal] = useState(false);
  const [bankConnectedIsOpen, setBankConnectedIsOpen] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [taxClassification, setTaxClassification] = useState(
    TaxClassification.individual
  );
  const [ein, setEin] = useState('');
  const [ssn, setSsn] = useState('');
  const [step, setStep] = useState(Step.AboutYou);
  const [accountType, setAccountType] = useState(AccountType.business);
  const [routingNumber, setRoutingNumber] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [confirmAccountNumber, setConfirmAccountNumber] = useState('');
  const [termsAgreed, setTermsAgreed] = useState(false);

  const [businessType, setBusinessType] = useState(BusinessType.soleProprietor);

  const [addressObject, setAddressObject] =
    useState<CreateManualDeliveryAddressData | null>(null);

  const formattedAddress = addressObject
    ? `${addressObject.streetNumber} ${addressObject.address} ${addressObject.city} ${addressObject.zipCode} ${addressObject.state} ${addressObject.country}`
    : '';

  const { user: currentUser } = useAuth((state) => state);

  useEffect(() => {
    if (isPartner) {
      setFirstName(currentUser?.firstName || '');
      setLastName(currentUser?.lastName || '');
      setEmail(currentUser?.email || '');
    } else {
      setFirstName(user?.firstName || '');
      setLastName(user?.lastName || '');
      setEmail(user?.email || '');
    }
  }, [currentUser, modalIsOpen, user]);

  const handleAddBankAccount = async () => {
    setLoading(true);

    const body = {
      locationId: (wallet as Wallets)?.simpleLocation?.id,
      firstName,
      lastName,
      email,
      routingNumber,
      accountNumber,
      businessName: accountType === AccountType.business ? businessName : null,
      phoneNumber,
      zipCode: addressObject?.zipCode || '',
      address: formattedAddress,
      streetNumber: addressObject?.streetNumber || '',
      city: addressObject?.city || '',
      state: addressObject?.state || '',
      taxClassification:
        accountType === AccountType.business
          ? taxClassification
          : TaxClassification.individual,
      ein: ein ? ein : undefined,
      ssn: ssn ? ssn : undefined,
    };

    try {
      if (wallet.isBankConnected) {
        const { data } = await editBankAccountV2(body);
        SuccessToast(data.message);
        setWallet(data.data);
      } else {
        if (isAdmin) {
          if (userRole === UserRole.partner) {
            const { data } = await userAddBankAccountV2Partner(
              userId as number,
              body
            );
            SuccessToast(data.message);
            setWallet(data.data);
          } else {
            const bodyAgent = {
              ...body,
              locationId: undefined,
            };
            const { data } = await userAddBankAccountV2(
              userId as number,
              bodyAgent
            );
            SuccessToast(data.message);
            setWallet({ ...wallet, isBankConnected: true });
          }
        } else {
          const { data } = await addBankAccountV2(body);
          SuccessToast(data.message);
          setWallet(data.data);
        }
      }

      setModalIsOpen(null);
      setBankConnectedIsOpen(true);
      setFirstName('');
      setLastName('');
      setEmail('');
      setBusinessName('');
      setPhoneNumber('');
      setTaxClassification(TaxClassification.individual);
      setEin('');
      setSsn('');
      setRoutingNumber('');
      setAccountNumber('');
      setBusinessType(BusinessType.soleProprietor);
      setAddressObject(null);
      setTermsAgreed(false);
      setStep(Step.AboutYou);
      setConfirmAccountNumber('');
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  };

  const showSsnBusinessInfo = () =>
    businessType === BusinessType.soleProprietor ||
    (businessType === BusinessType.llc &&
      taxClassification === TaxClassification.singleMemberLlc);

  const showEinBusinessInfo = () =>
    (businessType === BusinessType.llc &&
      taxClassification !== TaxClassification.singleMemberLlc) ||
    businessType === BusinessType.corporation ||
    businessType === BusinessType.partnership ||
    businessType === BusinessType.estate ||
    businessType === BusinessType.trust;

  const getErrMsgAboutYouScreen = () => {
    if (!firstName.trim()) return 'Please enter the first name';
    if (!lastName.trim()) return 'Please enter the last name';
    if (!isEmailValid(email)) return 'Please enter a valid email';
    if (!formattedAddress.trim()) return 'Please enter the address';
    if (!isValidPhoneNumber(phoneNumber ?? ''))
      return 'Please enter a valid phone number';

    if (accountType === AccountType.individual) {
      if (!ssn.trim() || ssn.trim().length < 11)
        return 'Please enter a valid SSN';
    }
    return null;
  };

  const getErrMsgBusinessInfoScreen = () => {
    if (!businessName.trim()) return 'Please enter the legal business name';
    if (accountType === AccountType.business) {
      if (showSsnBusinessInfo()) {
        if (!ssn.trim() || ssn.trim().length < 11)
          return 'Please enter a valid SSN';
      }
      if (showEinBusinessInfo()) {
        if (!ein.trim() || ein.trim().length < 10)
          return 'Please enter a valid EIN';
      }
    }
    return null;
  };

  const getErrMsgBankInfoScreen = () => {
    if (!routingNumber.trim().length || routingNumber.trim().length !== 9)
      return 'Please enter valid routing number. (9 digits)';
    if (!accountNumber.trim()) return 'Please enter the account number';
    if (accountNumber !== confirmAccountNumber)
      return 'Account numbers do not match. Please check and try again.';
    return null;
  };

  const getErrMsgConfirmationScreen = () => {
    if (!termsAgreed) return 'Please accept vGrubs terms';
    return null;
  };

  const handleNextStep = () => {
    if (step === Step.AboutYou) {
      if (accountType === AccountType.individual) {
        setStep(Step.BankInfo);
      } else {
        if (taxClassification === TaxClassification.individual) {
          setTaxClassification(TaxClassification.soleProprietor);
        }
        setStep(Step.BusinessInfo);
      }
    } else if (step === Step.BusinessInfo) {
      setStep(Step.BankInfo);
    } else if (step === Step.BankInfo) {
      setStep(Step.Confirmation);
    } else if (step === Step.Confirmation) {
      handleAddBankAccount();
    }
  };

  const handleNext = () => {
    switch (step) {
      case Step.AboutYou: {
        const error = getErrMsgAboutYouScreen();
        if (error) {
          return ErrorToast(error);
        }
        break;
      }
      case Step.BusinessInfo: {
        const error = getErrMsgBusinessInfoScreen();
        if (error) {
          return ErrorToast(error);
        }
        break;
      }
      case Step.BankInfo: {
        const error = getErrMsgBankInfoScreen();
        if (error) {
          return ErrorToast(error);
        }
        break;
      }
      case Step.Confirmation: {
        const error = getErrMsgConfirmationScreen();
        if (error) {
          return ErrorToast(error);
        }
        break;
      }
    }

    handleNextStep();
  };

  const handleBack = () => {
    switch (step) {
      case Step.AboutYou:
        setModalIsOpen(null);
        setInitialBankModal(true);
        break;
      case Step.BusinessInfo:
        setStep(Step.AboutYou);
        break;
      case Step.BankInfo:
        if (accountType === AccountType.individual) {
          setStep(Step.AboutYou);
        } else setStep(Step.BusinessInfo);
        break;
      case Step.Confirmation:
        setStep(Step.BankInfo);
        break;
    }
  };

  return {
    firstName,
    lastName,
    email,
    businessName,
    phoneNumber,
    taxClassification,
    ein,
    ssn,
    setFirstName,
    setLastName,
    setEmail,
    setBusinessName,
    setPhoneNumber,
    setTaxClassification,
    setEin,
    setSsn,
    handleNext,
    handleBack,
    step,
    setStep,
    accountType,
    setAccountType,
    termsAgreed,
    setTermsAgreed,
    routingNumber,
    setRoutingNumber,
    accountNumber,
    setAccountNumber,
    businessType,
    setBusinessType,
    getErrMsgAboutYouScreen,
    getErrMsgBusinessInfoScreen,
    getErrMsgBankInfoScreen,
    termsIsOpen,
    setTermsIsOpen,
    initialBankModal,
    setInitialBankModal,
    loading,
    setLoading,
    setAddressObject,
    addressObject,
    formattedAddress,
    bankConnectedIsOpen,
    setBankConnectedIsOpen,
    confirmAccountNumber,
    setConfirmAccountNumber,
  };
};
